import React, { useEffect, useState, useRef } from 'react';
import { ArrowLeft, Package, CheckCircle, Edit2, AlertTriangle, Loader2, Sparkles } from 'lucide-react';
import { callWFAPI } from '../utils/helper';
import { InternalNotesButton } from './InternalNotesButton';
interface Rental {
  id: string;
  name: string;
  email: string;
  phone: string;
  eventName: string;
  customerInfo?: {
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    eventVenueCityState?: string;
    preferredTechnician?: string;
  };
  proposedEventDate: string;
  alternateDate: string;
  status: 'Pending' | 'Confirmed' | 'Deposit' | 'Paid in Full';
  total: number;
  subtotal: number;
  discount: number;
  tax: number;
  added: string;
  assignedTechnician?: string;
  assignedDistributor?: string;
  invoiceId?: string;
  internalNotes?: string;
  selections: {
    gen3Body: number;
    gen3Head: number;
    gen3JudgesOnly: number;
    fullGen3Ring: number;
    requireTech: boolean;
    numberOfDays?: number;
  };
}
interface DispatchManagerProps {
  rentals: Rental[];
  getStatusColor: (status: string) => string;
  logActivity: (category: 'technician' | 'distributor' | 'invoice' | 'rental' | 'system' | 'dispatch' | 'onsiteprep', action: string, details: string) => Promise<void>;
  onNotesUpdated?: () => void;
}
export function DispatchManager({
  rentals,
  getStatusColor,
  logActivity,
  onNotesUpdated
}: DispatchManagerProps) {
  const [selectedDispatchEvent, setSelectedDispatchEvent] = useState<Rental | null>(null);
  const [preEventChecklist, setPreEventChecklist] = useState({
    fullRadioSets: 0,
    radioSetsTested: false,
    spareDevices: '',
    redChest0: 0,
    blueChest0: 0,
    redChest1: 0,
    blueChest1: 0,
    redChest2: 0,
    blueChest2: 0,
    redChest3: 0,
    blueChest3: 0,
    redChest4: 0,
    blueChest4: 0,
    redChest5: 0,
    blueChest5: 0,
    redHeadXS: 0,
    blueHeadXS: 0,
    redHeadS: 0,
    blueHeadS: 0,
    redHeadM: 0,
    blueHeadM: 0,
    redHeadL: 0,
    blueHeadL: 0,
    redHeadXL: 0,
    blueHeadXL: 0,
    spareRJ45Clips: false,
    chargers: false,
    rj45CrimpTool: false,
    spareAntennas: false,
    devicesFullyCharged: false
  });
  const [checklistSignature, setChecklistSignature] = useState<string>('');
  const [isChecklistLocked, setIsChecklistLocked] = useState(false);
  const [checklistCollapsed, setChecklistCollapsed] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [showEditReasonModal, setShowEditReasonModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLoadingChecklist, setIsLoadingChecklist] = useState(false);
  const [existingChecklistId, setExistingChecklistId] = useState<string | null>(null);
  const signatureCanvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawingSignature, setIsDrawingSignature] = useState(false);
  // Helper function to draw signature on canvas
  const drawSignatureOnCanvas = (signatureData: string) => {
    const canvas = signatureCanvasRef.current;
    if (canvas && signatureData) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
        };
        img.onerror = () => {
          console.error('Failed to load signature image');
        };
        img.src = signatureData;
      }
    }
  };
  // Redraw signature when canvas becomes visible (form expanded)
  useEffect(() => {
    if (!checklistCollapsed && checklistSignature && signatureCanvasRef.current) {
      drawSignatureOnCanvas(checklistSignature);
    }
  }, [checklistCollapsed, checklistSignature]);
  // Initialize canvas with white background
  useEffect(() => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, []);
  // Load existing checklist when rental is selected
  useEffect(() => {
    const loadExistingChecklist = async () => {
      if (!selectedDispatchEvent) return;
      setIsLoadingChecklist(true);
      try {
        const data = {
          appname: 'tkd-score',
          reportname: 'Gen_3_Scoring_Pre_Event_Checklist_Report',
          criteria: `Gen_3_Scoring_Rental == ${selectedDispatchEvent.id}`
        };
        const checklists = await callWFAPI(0, 'zohoCreator', data);
        const checklist = checklists[0];
        if (checklist) {
          // Existing checklist found - load data into form
          setExistingChecklistId(checklist.ID);
          setPreEventChecklist({
            fullRadioSets: parseInt(checklist.Full_Radio_Sets) || 0,
            radioSetsTested: true,
            spareDevices: checklist.Spare_Devices || '',
            redChest0: parseInt(checklist.Red_Chest_0) || 0,
            blueChest0: parseInt(checklist.Blue_Chest_0) || 0,
            redChest1: parseInt(checklist.Red_Chest_1) || 0,
            blueChest1: parseInt(checklist.Blue_Chest_1) || 0,
            redChest2: parseInt(checklist.Red_Chest_2) || 0,
            blueChest2: parseInt(checklist.Blue_Chest_2) || 0,
            redChest3: parseInt(checklist.Red_Chest_3) || 0,
            blueChest3: parseInt(checklist.Blue_Chest_3) || 0,
            redChest4: parseInt(checklist.Red_Chest_4) || 0,
            blueChest4: parseInt(checklist.Blue_Chest_4) || 0,
            redChest5: parseInt(checklist.Red_Chest_5) || 0,
            blueChest5: parseInt(checklist.Blue_Chest_5) || 0,
            redHeadXS: parseInt(checklist.Red_Head_XS) || 0,
            blueHeadXS: parseInt(checklist.Blue_Head_XS) || 0,
            redHeadS: parseInt(checklist.Red_Head_S) || 0,
            blueHeadS: parseInt(checklist.Blue_Head_S) || 0,
            redHeadM: parseInt(checklist.Red_Head_M) || 0,
            blueHeadM: parseInt(checklist.Blue_Head_M) || 0,
            redHeadL: parseInt(checklist.Red_Head_L) || 0,
            blueHeadL: parseInt(checklist.Blue_Head_L) || 0,
            redHeadXL: parseInt(checklist.Red_Head_XL) || 0,
            blueHeadXL: parseInt(checklist.Blue_Head_XL) || 0,
            spareRJ45Clips: true,
            chargers: true,
            rj45CrimpTool: true,
            spareAntennas: true,
            devicesFullyCharged: true
          });
          // Load signature
          if (checklist.Signature) {
            setChecklistSignature(checklist.Signature);
            // Draw signature on canvas will happen in the separate useEffect
          }
          // Set to locked/edit mode
          setIsChecklistLocked(true);
          setChecklistCollapsed(true);
        } else {
          // No existing checklist - reset to empty form
          setExistingChecklistId(null);
          setPreEventChecklist({
            fullRadioSets: 0,
            radioSetsTested: false,
            spareDevices: '',
            redChest0: 0,
            blueChest0: 0,
            redChest1: 0,
            blueChest1: 0,
            redChest2: 0,
            blueChest2: 0,
            redChest3: 0,
            blueChest3: 0,
            redChest4: 0,
            blueChest4: 0,
            redChest5: 0,
            blueChest5: 0,
            redHeadXS: 0,
            blueHeadXS: 0,
            redHeadS: 0,
            blueHeadS: 0,
            redHeadM: 0,
            blueHeadM: 0,
            redHeadL: 0,
            blueHeadL: 0,
            redHeadXL: 0,
            blueHeadXL: 0,
            spareRJ45Clips: false,
            chargers: false,
            rj45CrimpTool: false,
            spareAntennas: false,
            devicesFullyCharged: false
          });
          setChecklistSignature('');
          setIsChecklistLocked(false);
          setChecklistCollapsed(false);
        }
      } catch (error) {
        console.error('Error loading checklist:', error);
      } finally {
        setIsLoadingChecklist(false);
      }
    };
    loadExistingChecklist();
  }, [selectedDispatchEvent]);
  const handleRadioSetsChange = (value: number) => {
    setPreEventChecklist({
      ...preEventChecklist,
      fullRadioSets: value,
      redChest0: value,
      blueChest0: value,
      redChest1: value,
      blueChest1: value,
      redChest2: value,
      blueChest2: value,
      redChest3: value,
      blueChest3: value,
      redChest4: value,
      blueChest4: value,
      redChest5: value,
      blueChest5: value,
      redHeadXS: value,
      blueHeadXS: value,
      redHeadS: value,
      blueHeadS: value,
      redHeadM: value,
      blueHeadM: value,
      redHeadL: value,
      blueHeadL: value,
      redHeadXL: value,
      blueHeadXL: value
    });
  };
  const startDrawingChecklistSignature = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    // Prevent drawing if checklist is locked
    if (isChecklistLocked) return;
    setIsDrawingSignature(true);
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ('touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left) * scaleX;
    const y = ('touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top) * scaleY;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
  };
  const drawChecklistSignature = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawingSignature) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ('touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left) * scaleX;
    const y = ('touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top) * scaleY;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
  };
  const stopDrawingChecklistSignature = () => {
    setIsDrawingSignature(false);
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      setChecklistSignature(canvas.toDataURL());
    }
  };
  const clearChecklistSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
    setChecklistSignature('');
  };
  const handleEditChecklist = () => {
    setShowEditReasonModal(true);
  };
  const confirmEditChecklist = () => {
    if (!editReason.trim()) {
      alert('Please provide a reason for editing the checklist');
      return;
    }
    logActivity('dispatch', 'Checklist edit initiated', `Editing checklist for ${selectedDispatchEvent?.eventName}. Reason: ${editReason}`);
    setIsChecklistLocked(false);
    setChecklistCollapsed(false);
    // Clear the signature - user must re-sign when editing
    clearChecklistSignature();
    setShowEditReasonModal(false);
    setEditReason('');
  };
  const saveChecklist = async () => {
    if (!checklistSignature) {
      alert('Please provide your signature to confirm equipment verification');
      return;
    }
    if (!preEventChecklist.spareDevices.trim()) {
      alert('Spare Devices field cannot be blank. Enter device details or "NONE" if no spares.');
      return;
    }
    if (!preEventChecklist.radioSetsTested) {
      alert('You must verify all radio sets are matched to the receiver and tested');
      return;
    }
    if (!preEventChecklist.spareRJ45Clips) {
      alert('Spare RJ45 Clips must be checked');
      return;
    }
    if (!preEventChecklist.chargers) {
      alert('Chargers must be checked');
      return;
    }
    if (!preEventChecklist.rj45CrimpTool) {
      alert('RJ45 Crimp Tool must be checked');
      return;
    }
    if (!preEventChecklist.spareAntennas) {
      alert('Spare Antennas must be checked');
      return;
    }
    if (!preEventChecklist.devicesFullyCharged) {
      alert('You must verify all devices are fully charged');
      return;
    }
    if (!selectedDispatchEvent) {
      alert('No event selected');
      return;
    }
    setIsSaving(true);
    try {
      // Save checklist to database
      const checklistData = {
        Gen_3_Scoring_Rental: selectedDispatchEvent.id,
        Full_Radio_Sets: preEventChecklist.fullRadioSets,
        Edit_Reason: existingChecklistId && editReason ? editReason : '',
        Spare_Devices: preEventChecklist.spareDevices,
        Red_Chest_0: preEventChecklist.redChest0,
        Red_Chest_1: preEventChecklist.redChest1,
        Red_Chest_2: preEventChecklist.redChest2,
        Red_Chest_3: preEventChecklist.redChest3,
        Red_Chest_4: preEventChecklist.redChest4,
        Red_Chest_5: preEventChecklist.redChest5,
        Blue_Chest_0: preEventChecklist.blueChest0,
        Blue_Chest_1: preEventChecklist.blueChest1,
        Blue_Chest_2: preEventChecklist.blueChest2,
        Blue_Chest_3: preEventChecklist.blueChest3,
        Blue_Chest_4: preEventChecklist.blueChest4,
        Blue_Chest_5: preEventChecklist.blueChest5,
        Red_Head_XS: preEventChecklist.redHeadXS,
        Red_Head_S: preEventChecklist.redHeadS,
        Red_Head_M: preEventChecklist.redHeadM,
        Red_Head_L: preEventChecklist.redHeadL,
        Red_Head_XL: preEventChecklist.redHeadXL,
        Blue_Head_XS: preEventChecklist.blueHeadXS,
        Blue_Head_S: preEventChecklist.blueHeadS,
        Blue_Head_M: preEventChecklist.blueHeadM,
        Blue_Head_L: preEventChecklist.blueHeadL,
        Blue_Head_XL: preEventChecklist.blueHeadXL,
        Signature: checklistSignature
      };
      let data;
      if (existingChecklistId) {
        // Update existing checklist
        data = {
          appname: 'tkd-score',
          reportname: 'Gen_3_Scoring_Pre_Event_Checklist_Report',
          id: existingChecklistId,
          params: JSON.stringify(checklistData)
        };
      } else {
        // Create new checklist
        data = {
          appname: 'tkd-score',
          formname: 'Gen_3_Scoring_Pre_Event_Checklist',
          params: JSON.stringify(checklistData)
        };
      }
      const result = await callWFAPI(0, 'zohoCreator', data);
      // Store the ID if this was a new checklist
      if (!existingChecklistId && result?.ID) {
        setExistingChecklistId(result.ID);
      }
      const reason = isChecklistLocked ? editReason : 'Initial checklist';
      // Build detailed checklist summary
      const checklistSummary = `Radio Sets: ${preEventChecklist.fullRadioSets}, Radio Sets Tested: ${preEventChecklist.radioSetsTested ? 'Yes' : 'No'}, Spare Devices: ${preEventChecklist.spareDevices}, Red/Blue Chest 0-5: ${preEventChecklist.redChest0}/${preEventChecklist.blueChest0}, ${preEventChecklist.redChest1}/${preEventChecklist.blueChest1}, ${preEventChecklist.redChest2}/${preEventChecklist.blueChest2}, ${preEventChecklist.redChest3}/${preEventChecklist.blueChest3}, ${preEventChecklist.redChest4}/${preEventChecklist.blueChest4}, ${preEventChecklist.redChest5}/${preEventChecklist.blueChest5}, Red/Blue Head XS-XL: ${preEventChecklist.redHeadXS}/${preEventChecklist.blueHeadXS}, ${preEventChecklist.redHeadS}/${preEventChecklist.blueHeadS}, ${preEventChecklist.redHeadM}/${preEventChecklist.blueHeadM}, ${preEventChecklist.redHeadL}/${preEventChecklist.blueHeadL}, ${preEventChecklist.redHeadXL}/${preEventChecklist.blueHeadXL}, RJ45 Clips: ${preEventChecklist.spareRJ45Clips ? 'Yes' : 'No'}, Chargers: ${preEventChecklist.chargers ? 'Yes' : 'No'}, RJ45 Crimp Tool: ${preEventChecklist.rj45CrimpTool ? 'Yes' : 'No'}, Spare Antennas: ${preEventChecklist.spareAntennas ? 'Yes' : 'No'}, Devices Fully Charged: ${preEventChecklist.devicesFullyCharged ? 'Yes' : 'No'}`;
      await logActivity('dispatch', isChecklistLocked ? 'Checklist updated' : 'Checklist saved', `${isChecklistLocked ? 'Updated' : 'Saved'} checklist for ${selectedDispatchEvent?.eventName}. ${isChecklistLocked ? `Edit Reason: ${reason}. ` : ''}Checklist: ${checklistSummary}. Signature captured.`);
      setIsChecklistLocked(true);
      setChecklistCollapsed(true);
      setEditReason('');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving checklist:', error);
      alert('Failed to save checklist. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  const eligibleRentals = rentals.filter(rental => rental.invoiceId && rental.assignedTechnician);
  if (!selectedDispatchEvent) {
    return <div className="p-6" data-id="element-558">
        <div className="mb-6" data-id="element-559">
          <h2 className="text-xl font-semibold text-slate-800 mb-2" data-id="element-560">
            Dispatch Management
          </h2>
          <p className="text-sm text-slate-600" data-id="element-561">
            Manage pre-event checklists for invoiced events with assigned
            technicians
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-562">
          {eligibleRentals.map(rental => <button key={rental.id} onClick={() => setSelectedDispatchEvent(rental)} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-lg hover:border-blue-300 transition-all text-left relative" data-id="element-563">
              <div className="absolute top-3 right-3" data-id="element-564">
                <InternalNotesButton rentalId={rental.id} initialNotes={rental.internalNotes} onNotesSaved={onNotesUpdated} data-id="element-565" />
              </div>
              <div className="flex items-start justify-between mb-2 pr-10" data-id="element-566">
                <h3 className="font-semibold text-slate-800 text-lg" data-id="element-567">
                  {rental.eventName}
                </h3>
                <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(rental.status)}`} data-id="element-568">
                  {rental.status}
                </span>
              </div>
              <div className="space-y-1 text-sm text-slate-600" data-id="element-569">
                <p data-id="element-570">Customer: {rental.name}</p>
                <p data-id="element-571">Technician: {rental.assignedTechnician}</p>
                <p className="flex items-center gap-2" data-id="element-572">
                  <span data-id="element-573">Date:</span>
                  <span data-id="element-574">
                    {rental.proposedEventDate ? new Date(rental.proposedEventDate).toLocaleDateString() : 'TBD'}
                  </span>
                  {rental.selections.numberOfDays && <span className="bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-0.5 rounded" data-id="element-575">
                      {rental.selections.numberOfDays}{' '}
                      {rental.selections.numberOfDays === 1 ? 'Day' : 'Days'}
                    </span>}
                </p>
                <p className="text-xs text-blue-700 font-semibold mt-2" data-id="element-576">
                  {rental.selections.fullGen3Ring} Ring
                  {rental.selections.fullGen3Ring !== 1 ? 's' : ''}
                </p>
              </div>
            </button>)}
        </div>

        {eligibleRentals.length === 0 && <div className="text-center py-12" data-id="element-577">
            <Package size={48} className="text-slate-300 mx-auto mb-4" data-id="element-578" />
            <p className="text-slate-600" data-id="element-579">No events ready for dispatch</p>
            <p className="text-sm text-slate-500 mt-1" data-id="element-580">
              Events must be invoiced and have an assigned technician
            </p>
          </div>}
      </div>;
  }
  // Render the full dispatch checklist form
  return <div className="p-6" data-id="element-581">
      <div className="mb-6" data-id="element-582">
        <button onClick={() => setSelectedDispatchEvent(null)} className="flex items-center text-slate-600 hover:text-slate-800 transition-colors mb-4" data-id="element-583">
          <ArrowLeft size={20} className="mr-2" data-id="element-584" />
          Back to Events
        </button>

        <div className="flex items-start justify-between" data-id="element-585">
          <div data-id="element-586">
            <h2 className="text-xl font-semibold text-slate-800 mb-1" data-id="element-587">
              Pre-Event Checklist
            </h2>
            <p className="text-sm text-slate-600" data-id="element-588">
              {selectedDispatchEvent.eventName} - {selectedDispatchEvent.name}
            </p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(selectedDispatchEvent.status)}`} data-id="element-589">
            {selectedDispatchEvent.status}
          </span>
        </div>
      </div>

      {/* Event Details Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-590">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm" data-id="element-591">
          <div data-id="element-592">
            <span className="text-blue-700 font-medium" data-id="element-593">Date:</span>
            <p className="text-blue-900" data-id="element-594">
              {selectedDispatchEvent.proposedEventDate ? new Date(selectedDispatchEvent.proposedEventDate).toLocaleDateString() : 'TBD'}
            </p>
          </div>
          <div data-id="element-595">
            <span className="text-blue-700 font-medium" data-id="element-596">Technician:</span>
            <p className="text-blue-900" data-id="element-597">
              {selectedDispatchEvent.assignedTechnician}
            </p>
          </div>
          <div data-id="element-598">
            <span className="text-blue-700 font-medium" data-id="element-599">Equipment:</span>
            <p className="text-blue-900" data-id="element-600">
              {selectedDispatchEvent.selections.fullGen3Ring} Full Ring
              {selectedDispatchEvent.selections.fullGen3Ring !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Checklist Form */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-id="element-601">
        {/* Loading State */}
        {isLoadingChecklist && <div className="p-12 flex flex-col items-center justify-center" data-id="element-602">
            <Loader2 size={48} className="animate-spin text-blue-600 mb-4" data-id="element-603" />
            <p className="text-slate-600 font-medium" data-id="element-604">Loading checklist...</p>
            <p className="text-sm text-slate-500 mt-1" data-id="element-605">Please wait</p>
          </div>}

        {/* Checklist Content (hidden while loading) */}
        {!isLoadingChecklist && <>
            {/* Checklist Header */}
            <div className="bg-slate-50 border-b border-slate-200 p-4 flex items-center justify-between" data-id="element-606">
              <div className="flex items-center gap-3" data-id="element-607">
                <Package className="text-blue-600" size={24} data-id="element-608" />
                <div data-id="element-609">
                  <h3 className="font-semibold text-slate-800" data-id="element-610">
                    Equipment Verification
                  </h3>
                  <p className="text-xs text-slate-600" data-id="element-611">
                    Complete all fields before dispatch
                  </p>
                </div>
              </div>
              {isChecklistLocked && <button onClick={handleEditChecklist} className="flex items-center gap-2 px-3 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors text-sm font-medium" data-id="element-612">
                  <Edit2 size={16} data-id="element-613" />
                  Edit Checklist
                </button>}
            </div>

            {/* Checklist Content */}
            <div className={`p-6 space-y-6 ${isChecklistLocked && checklistCollapsed ? 'hidden' : ''}`} data-id="element-614">
              {/* Radio Sets */}
              <div className="space-y-3" data-id="element-615">
                <label className="block text-sm font-semibold text-slate-700" data-id="element-616">
                  Full Radio Sets <span className="text-red-500" data-id="element-617">*</span>
                </label>
                <input type="number" min="0" value={preEventChecklist.fullRadioSets} onChange={e => handleRadioSetsChange(parseInt(e.target.value) || 0)} disabled={isChecklistLocked} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-100 disabled:cursor-not-allowed" placeholder="Enter number of radio sets" data-id="element-618" />
                <div className="flex items-center gap-2 mt-2" data-id="element-619">
                  <input type="checkbox" id="radioSetsTested" checked={preEventChecklist.radioSetsTested} onChange={e => setPreEventChecklist({
                ...preEventChecklist,
                radioSetsTested: e.target.checked
              })} disabled={isChecklistLocked} className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed" data-id="element-620" />
                  <label htmlFor="radioSetsTested" className="text-sm text-slate-700" data-id="element-621">
                    All radio sets matched to receiver and tested{' '}
                    <span className="text-red-500" data-id="element-622">*</span>
                  </label>
                </div>
              </div>

              {/* Spare Devices */}
              <div className="space-y-3" data-id="element-623">
                <label className="block text-sm font-semibold text-slate-700" data-id="element-624">
                  Spare Devices <span className="text-red-500" data-id="element-625">*</span>
                </label>
                <textarea value={preEventChecklist.spareDevices} onChange={e => setPreEventChecklist({
              ...preEventChecklist,
              spareDevices: e.target.value
            })} disabled={isChecklistLocked} rows={3} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-100 disabled:cursor-not-allowed" placeholder='Enter spare device details or "NONE"' data-id="element-626" />
              </div>

              {/* Chest Protectors */}
              <div className="space-y-4" data-id="element-627">
                <h4 className="font-semibold text-slate-700" data-id="element-628">
                  Chest Protectors (Red/Blue)
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-629">
                  {[0, 1, 2, 3, 4, 5].map(size => <div key={size} className="space-y-2" data-id="element-630">
                      <label className="block text-sm font-medium text-slate-700" data-id="element-631">
                        Size {size}
                      </label>
                      <div className="grid grid-cols-2 gap-2" data-id="element-632">
                        <input type="number" min="0" value={preEventChecklist[`redChest${size}`]} onChange={e => setPreEventChecklist({
                    ...preEventChecklist,
                    [`redChest${size}`]: parseInt(e.target.value) || 0
                  })} disabled={isChecklistLocked} className="w-full px-3 py-2 border border-red-300 bg-red-50 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-slate-100 disabled:cursor-not-allowed text-sm" placeholder="Red" data-id="element-633" />
                        <input type="number" min="0" value={preEventChecklist[`blueChest${size}`]} onChange={e => setPreEventChecklist({
                    ...preEventChecklist,
                    [`blueChest${size}`]: parseInt(e.target.value) || 0
                  })} disabled={isChecklistLocked} className="w-full px-3 py-2 border border-blue-300 bg-blue-50 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-100 disabled:cursor-not-allowed text-sm" placeholder="Blue" data-id="element-634" />
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Head Gear */}
              <div className="space-y-4" data-id="element-635">
                <h4 className="font-semibold text-slate-700" data-id="element-636">
                  Head Gear (Red/Blue)
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-637">
                  {['XS', 'S', 'M', 'L', 'XL'].map(size => <div key={size} className="space-y-2" data-id="element-638">
                      <label className="block text-sm font-medium text-slate-700" data-id="element-639">
                        {size}
                      </label>
                      <div className="grid grid-cols-2 gap-2" data-id="element-640">
                        <input type="number" min="0" value={preEventChecklist[`redHead${size}`]} onChange={e => setPreEventChecklist({
                    ...preEventChecklist,
                    [`redHead${size}`]: parseInt(e.target.value) || 0
                  })} disabled={isChecklistLocked} className="w-full px-3 py-2 border border-red-300 bg-red-50 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-slate-100 disabled:cursor-not-allowed text-sm" placeholder="Red" data-id="element-641" />
                        <input type="number" min="0" value={preEventChecklist[`blueHead${size}`]} onChange={e => setPreEventChecklist({
                    ...preEventChecklist,
                    [`blueHead${size}`]: parseInt(e.target.value) || 0
                  })} disabled={isChecklistLocked} className="w-full px-3 py-2 border border-blue-300 bg-blue-50 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-100 disabled:cursor-not-allowed text-sm" placeholder="Blue" data-id="element-642" />
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Additional Equipment Checklist */}
              <div className="space-y-3" data-id="element-643">
                <h4 className="font-semibold text-slate-700" data-id="element-644">
                  Additional Equipment
                </h4>
                <div className="space-y-2" data-id="element-645">
                  {[{
                key: 'spareRJ45Clips',
                label: 'Spare RJ45 Clips'
              }, {
                key: 'chargers',
                label: 'Chargers'
              }, {
                key: 'rj45CrimpTool',
                label: 'RJ45 Crimp Tool'
              }, {
                key: 'spareAntennas',
                label: 'Spare Antennas'
              }, {
                key: 'devicesFullyCharged',
                label: 'All Devices Fully Charged'
              }].map(item => <div key={item.key} className="flex items-center gap-2" data-id="element-646">
                      <input type="checkbox" id={item.key} checked={preEventChecklist[item.key]} onChange={e => setPreEventChecklist({
                  ...preEventChecklist,
                  [item.key]: e.target.checked
                })} disabled={isChecklistLocked} className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed" data-id="element-647" />
                      <label htmlFor={item.key} className="text-sm text-slate-700" data-id="element-648">
                        {item.label} <span className="text-red-500" data-id="element-649">*</span>
                      </label>
                    </div>)}
                </div>
              </div>

              {/* Signature */}
              <div className="space-y-3" data-id="element-650">
                <label className="block text-sm font-semibold text-slate-700" data-id="element-651">
                  Signature <span className="text-red-500" data-id="element-652">*</span>
                </label>
                <div className={`border-2 rounded-lg overflow-hidden ${isChecklistLocked ? 'border-slate-200 bg-slate-50' : 'border-slate-300 bg-white'}`} data-id="element-653">
                  <canvas ref={signatureCanvasRef} width={600} height={200} onMouseDown={isChecklistLocked ? undefined : startDrawingChecklistSignature} onMouseMove={isChecklistLocked ? undefined : drawChecklistSignature} onMouseUp={isChecklistLocked ? undefined : stopDrawingChecklistSignature} onMouseLeave={isChecklistLocked ? undefined : stopDrawingChecklistSignature} onTouchStart={isChecklistLocked ? undefined : startDrawingChecklistSignature} onTouchMove={isChecklistLocked ? undefined : drawChecklistSignature} onTouchEnd={isChecklistLocked ? undefined : stopDrawingChecklistSignature} className={`w-full ${isChecklistLocked ? 'cursor-default' : 'cursor-crosshair touch-none'}`} style={{
                touchAction: isChecklistLocked ? 'auto' : 'none'
              }} data-id="element-654" />
                </div>
                {!isChecklistLocked && <button onClick={clearChecklistSignature} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-655">
                    Clear Signature
                  </button>}
              </div>

              {/* Save Button */}
              <div className="pt-4 border-t border-slate-200" data-id="element-656">
                <button onClick={saveChecklist} disabled={isChecklistLocked || isSaving} className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:bg-slate-300 disabled:cursor-not-allowed" data-id="element-657">
                  {isSaving ? <>
                      <Loader2 size={20} className="animate-spin" data-id="element-658" />
                      Saving Checklist...
                    </> : <>
                      <CheckCircle size={20} data-id="element-659" />
                      {isChecklistLocked ? 'Checklist Saved' : 'Save Checklist'}
                    </>}
                </button>
              </div>
            </div>

            {/* Collapsed View */}
            {isChecklistLocked && checklistCollapsed && <div className="p-6" data-id="element-660">
                <div className="flex items-center justify-between" data-id="element-661">
                  <div className="flex items-center gap-3" data-id="element-662">
                    <CheckCircle className="text-green-600" size={24} data-id="element-663" />
                    <div data-id="element-664">
                      <p className="font-semibold text-slate-800" data-id="element-665">
                        Checklist Completed
                      </p>
                      <p className="text-sm text-slate-600" data-id="element-666">
                        Equipment verified and ready for dispatch
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setChecklistCollapsed(false)} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-667">
                    View Details
                  </button>
                </div>
              </div>}
          </>}
      </div>

      {/* Edit Reason Modal */}
      {showEditReasonModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-668">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md" data-id="element-669">
            <div className="p-6" data-id="element-670">
              <div className="flex items-start mb-4" data-id="element-671">
                <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0 mt-1" data-id="element-672" />
                <div data-id="element-673">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2" data-id="element-674">
                    Edit Checklist
                  </h3>
                  <p className="text-slate-600 text-sm" data-id="element-675">
                    Please provide a reason for editing this completed
                    checklist.
                  </p>
                </div>
              </div>

              <textarea value={editReason} onChange={e => setEditReason(e.target.value)} rows={4} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4" placeholder="Enter reason for editing..." data-id="element-676" />

              <div className="flex gap-3" data-id="element-677">
                <button onClick={() => {
              setShowEditReasonModal(false);
              setEditReason('');
            }} className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-678">
                  Cancel
                </button>
                <button onClick={confirmEditChecklist} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-679">
                  Confirm Edit
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Success Modal */}
      {showSuccessModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-680">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden" data-id="element-681">
            {/* Success Header with Gradient */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-6 text-center" data-id="element-682">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4" data-id="element-683">
                <CheckCircle className="text-green-600" size={32} data-id="element-684" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-685">
                Checklist Saved!
              </h3>
              <div className="flex items-center justify-center gap-1 text-green-50" data-id="element-686">
                <Sparkles size={16} data-id="element-687" />
                <p className="text-sm" data-id="element-688">Equipment verified successfully</p>
                <Sparkles size={16} data-id="element-689" />
              </div>
            </div>

            {/* Success Body */}
            <div className="p-6" data-id="element-690">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4" data-id="element-691">
                <div className="space-y-2 text-sm" data-id="element-692">
                  <div className="flex justify-between" data-id="element-693">
                    <span className="text-green-700 font-medium" data-id="element-694">Event:</span>
                    <span className="text-green-900 font-semibold" data-id="element-695">
                      {selectedDispatchEvent?.eventName}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-696">
                    <span className="text-green-700 font-medium" data-id="element-697">
                      Technician:
                    </span>
                    <span className="text-green-900" data-id="element-698">
                      {selectedDispatchEvent?.assignedTechnician}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-699">
                    <span className="text-green-700 font-medium" data-id="element-700">
                      Radio Sets:
                    </span>
                    <span className="text-green-900" data-id="element-701">
                      {preEventChecklist.fullRadioSets}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 mb-6" data-id="element-702">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} data-id="element-703" />
                <div className="text-sm text-slate-600" data-id="element-704">
                  <p className="font-medium text-slate-800 mb-1" data-id="element-705">
                    Ready for Dispatch
                  </p>
                  <p data-id="element-706">
                    All equipment has been verified and the checklist is now
                    locked. The event is ready for dispatch.
                  </p>
                </div>
              </div>

              <button onClick={() => setShowSuccessModal(false)} className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold" data-id="element-707">
                Continue
              </button>
            </div>
          </div>
        </div>}
    </div>;
}